// Save orchestrator. Picks the right save flow based on the tab's
// origin and the available filesystem capabilities, then reports
// what happened so main.ts can update tab state (markSaved / rename).
//
// Flow summary:
//   - file tab + writable source → write back through the source
//   - scratch tab + Chromium     → showSaveFilePicker, write via the
//                                  returned file handle
//   - scratch tab + other        → browser download (Blob + <a>)
//   - lesson tab                  → refused (toolbar disables Save)
//
// Permission failures on a writable source retry once via
// `verifyWritePermission`; if still denied we fall back to download
// rather than silently dropping the user's edits.

import {
  verifyWritePermission,
  type FolderSource,
} from './fs';
import type { TabInfo } from './tabs';

export interface SaveContext {
  /// Live editor text for the tab being saved.
  text: string;
  /// The tab's metadata (id, title, origin).
  tab: TabInfo;
  /// The currently-loaded folder source, if any. Used both to write
  /// `file:` tabs and to detect when a picked file lands inside the
  /// active folder (so we can re-id `untitled:N` → `file:<rel>`).
  source: FolderSource | null;
  /// Optional handle stashed on the tab from a prior `showSaveFilePicker`
  /// — lets repeated saves of an outside-the-folder untitled write
  /// through without re-prompting.
  fileHandle?: FileSystemFileHandle;
  /// Filename chosen by the in-app prompt (main.ts). Pre-fills the OS
  /// picker (Chromium) and names the download (Firefox) so the saved
  /// file matches the user's choice on every browser. Falls back to the
  /// tab title when absent.
  suggestedName?: string;
  /// Skip the in-place write-back paths (folder source + stashed handle)
  /// and go straight to picker/download — this is the Save As flow,
  /// where the user always wants to choose a fresh destination.
  forcePrompt?: boolean;
}

export type SaveOutcome =
  | { kind: 'wrote-folder'; path: string }
  | { kind: 'wrote-picker'; suggestedName: string; fileHandle: FileSystemFileHandle; rel: string | null }
  | { kind: 'wrote-handle'; suggestedName: string }
  | { kind: 'downloaded'; suggestedName: string }
  | { kind: 'cancelled' }
  | { kind: 'refused'; reason: string };

export async function saveTab(ctx: SaveContext): Promise<SaveOutcome> {
  const { text, tab, source, fileHandle, forcePrompt } = ctx;
  // The name to put in front of the user: the in-app prompt's choice if
  // present, else the tab title. Used by the picker AND both downloads
  // so the saved file is named consistently on every browser.
  const name = ctx.suggestedName ?? suggestedNameFor(tab);

  if (tab.origin === 'lesson') {
    return { kind: 'refused', reason: 'Lessons are read-only.' };
  }

  // file: tab — write back through the source if it supports it. Save As
  // (forcePrompt) skips this so the user can re-target a fresh file.
  if (!forcePrompt && tab.origin === 'file' && tab.id.startsWith('file:')) {
    const path = tab.id.slice('file:'.length);
    if (source && typeof source.write === 'function') {
      const ok = await ensureWritePermission(source);
      if (ok) {
        try {
          await source.write(path, text);
          return { kind: 'wrote-folder', path };
        } catch (e) {
          // Permission may have been revoked since the query — try
          // once more with an explicit request, then fall through.
          if (e instanceof DOMException && e.name === 'NotAllowedError') {
            const retried = await ensureWritePermission(source);
            if (retried) {
              await source.write(path, text);
              return { kind: 'wrote-folder', path };
            }
          }
          // Other failures (NotFoundError if file disappeared,
          // QuotaExceededError, etc.) propagate to the caller.
          throw e;
        }
      }
      // Permission denied — fall through to download so the user
      // doesn't lose their edits.
    }
    return downloadAs(name, text);
  }

  // scratch tab (untitled or boot scratch) — re-use the stashed handle
  // if we have one (repeated save of an outside-the-folder untitled).
  // Save As (forcePrompt) skips the stash so it always re-prompts.
  if (!forcePrompt && fileHandle) {
    try {
      await writeViaHandle(fileHandle, text);
      return { kind: 'wrote-handle', suggestedName: fileHandle.name };
    } catch (e) {
      // Handle may have gone stale (file deleted). Fall through to a
      // fresh picker so the user can re-save.
      if (!(e instanceof DOMException)) throw e;
    }
  }

  // No stash — try the OS save picker (Chromium only). Pre-fill it with
  // the chosen name so Chromium shows the same name the in-app prompt did.
  if (typeof (window as any).showSaveFilePicker === 'function') {
    let handle: FileSystemFileHandle;
    try {
      handle = await (window as any).showSaveFilePicker({
        suggestedName: name,
        types: [{
          description: 'SAS program',
          accept: { 'text/plain': ['.sas'] },
        }],
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        return { kind: 'cancelled' };
      }
      throw e;
    }
    await writeViaHandle(handle, text);
    // Try to figure out whether the picked file lives inside the
    // currently-loaded folder, so main.ts can promote the tab to a
    // `file:<rel>` id (and the tree highlight + future saves go
    // through the source). resolve() is async; null means outside.
    const rel = await relativePathInSource(handle, source);
    return {
      kind: 'wrote-picker',
      suggestedName: handle.name,
      fileHandle: handle,
      rel,
    };
  }

  // Firefox / no FS Access — download the file under the chosen name.
  return downloadAs(name, text);
}

async function writeViaHandle(handle: FileSystemFileHandle, text: string): Promise<void> {
  const writable = await handle.createWritable();
  await writable.write(text);
  await writable.close();
}

async function ensureWritePermission(source: FolderSource): Promise<boolean> {
  if (!source.rootHandle) return true; // non-FS-Access source — nothing to gate on
  const handle = source.rootHandle();
  const state = await verifyWritePermission(handle);
  return state === 'granted';
}

async function relativePathInSource(
  handle: FileSystemFileHandle,
  source: FolderSource | null,
): Promise<string | null> {
  if (!source?.rootHandle) return null;
  const root = source.rootHandle();
  const resolve = (root as any).resolve as
    | ((h: FileSystemHandle) => Promise<string[] | null>)
    | undefined;
  if (!resolve) return null;
  try {
    const segments = await resolve.call(root, handle);
    return segments && segments.length > 0 ? segments.join('/') : null;
  } catch {
    return null;
  }
}

function downloadAs(name: string, text: string): SaveOutcome {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { kind: 'downloaded', suggestedName: name };
}

function suggestedNameFor(tab: TabInfo): string {
  // Title is already the basename for file tabs; for untitled tabs
  // it's `untitled-N.sas`. Either way, normalise the extension.
  return ensureSasName(tab.title);
}

/// Normalise a filename to end in `.sas`. Empty input becomes
/// `untitled.sas`; a `.sas` name is returned unchanged. Shared by the
/// save flow and the in-app filename prompt so both agree on what a
/// valid SAS filename looks like.
export function ensureSasName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'untitled.sas';
  return trimmed.toLowerCase().endsWith('.sas') ? trimmed : `${trimmed}.sas`;
}
